import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError } from '@/services/api';
import type {
  ResellerDashboardData,
  ResellerClient,
  CreateClientRequest,
  CreateClientResponse,
  ClientFilters,
  ActivationCode,
  ResellerEarning
} from '@/types/reseller';

// Mock data for development
const mockResellerData: ResellerDashboardData = {
  profile: {
    id: 'reseller_1',
    name: 'João Silva',
    email: 'joao@exemplo.com',
    company: 'Silva Mídia',
    phone: '+55 11 99999-9999',
    tier: 'premium',
    status: 'active',
    joinedAt: '2024-01-15T00:00:00Z',
    lastLogin: '2024-12-19T10:30:00Z',
    commission: 25,
    totalEarnings: 15750.00,
    monthlyEarnings: 3250.00,
    totalClients: 150,
    activeClients: 142
  },
  stats: {
    totalClients: 150,
    activeClients: 142,
    suspendedClients: 3,
    expiredClients: 5,
    pendingClients: 0,
    totalRevenue: 63000.00,
    monthlyRevenue: 13000.00,
    commission: 25,
    totalEarnings: 15750.00,
    monthlyEarnings: 3250.00,
    conversionRate: 94.7,
    churnRate: 3.3
  },
  recentClients: [
    {
      id: 'client_1',
      name: 'Maria Santos',
      email: 'maria@email.com',
      status: 'active',
      plan: 'Premium',
      activatedAt: '2024-12-15T00:00:00Z',
      expiresAt: '2025-01-15T00:00:00Z',
      createdAt: '2024-12-15T00:00:00Z',
      totalPaid: 89.90,
      monthlyFee: 89.90
    },
    {
      id: 'client_2',
      name: 'Carlos Oliveira',
      email: 'carlos@email.com',
      status: 'active',
      plan: 'Basic',
      activatedAt: '2024-12-10T00:00:00Z',
      expiresAt: '2025-01-10T00:00:00Z',
      createdAt: '2024-12-10T00:00:00Z',
      totalPaid: 49.90,
      monthlyFee: 49.90
    }
  ],
  availablePlans: [
    {
      id: 'plan_basic',
      name: 'Basic',
      description: 'Plano básico com canais essenciais',
      duration: 30,
      price: 49.90,
      resellerPrice: 37.43,
      commission: 25,
      features: ['500+ Canais', '1 Conexão', 'Suporte Básico'],
      maxConnections: 1,
      isActive: true
    },
    {
      id: 'plan_premium',
      name: 'Premium',
      description: 'Plano premium com todos os canais',
      duration: 30,
      price: 89.90,
      resellerPrice: 67.43,
      commission: 25,
      features: ['1000+ Canais', '2 Conexões', 'Suporte Premium', 'VOD'],
      maxConnections: 2,
      isActive: true
    }
  ],
  recentCodes: [
    {
      id: 'code_1',
      code: 'MOOVA-2024-ABC123',
      plan: 'Premium',
      duration: 30,
      status: 'available',
      createdAt: '2024-12-19T00:00:00Z',
      expiresAt: '2025-01-19T00:00:00Z',
      price: 89.90
    }
  ]
};

// API functions with fallback to mock
const fetchResellerDashboard = async (): Promise<ResellerDashboardData> => {
  try {
    return await api.reseller.getDashboard();
  } catch (error) {
    console.warn('API call failed, using mock data:', error);
    await new Promise(resolve => setTimeout(resolve, 1000));
    return mockResellerData;
  }
};

const fetchResellerClients = async (filters?: ClientFilters): Promise<ResellerClient[]> => {
  try {
    return await api.reseller.getClients(filters);
  } catch (error) {
    console.warn('API call failed, using mock data:', error);
    await new Promise(resolve => setTimeout(resolve, 800));

    let clients = [...mockResellerData.recentClients];

    // Apply filters
    if (filters?.status && filters.status !== 'all') {
      clients = clients.filter(client => client.status === filters.status);
    }

    if (filters?.search) {
      const search = filters.search.toLowerCase();
      clients = clients.filter(client =>
        client.name.toLowerCase().includes(search) ||
        client.email.toLowerCase().includes(search)
      );
    }

    return clients;
  }
};

const createClient = async (request: CreateClientRequest): Promise<CreateClientResponse> => {
  try {
    return await api.reseller.createClient(request);
  } catch (error) {
    console.warn('API call failed, using mock data:', error);
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Mock validation
    if (!request.name || !request.email) {
      throw new Error('Nome e email são obrigatórios');
    }

    const activationCode = request.customCode || `MOOVA-${Date.now()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;

    return {
      success: true,
      message: 'Cliente criado com sucesso!',
      data: {
        clientId: `client_${Date.now()}`,
        activationCode,
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      }
    };
  }
};

const generateActivationCodes = async (planId: string, quantity: number): Promise<ActivationCode[]> => {
  try {
    return await api.reseller.generateCodes(planId, quantity);
  } catch (error) {
    console.warn('API call failed, using mock data:', error);
    await new Promise(resolve => setTimeout(resolve, 1500));

    const codes: ActivationCode[] = [];
    const plan = mockResellerData.availablePlans.find(p => p.id === planId);

    if (!plan) {
      throw new Error('Plano não encontrado');
    }

    for (let i = 0; i < quantity; i++) {
      codes.push({
        id: `code_${Date.now()}_${i}`,
        code: `MOOVA-${Date.now()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
        plan: plan.name,
        duration: plan.duration,
        status: 'available',
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year validity
        price: plan.price
      });
    }

    return codes;
  }
};

export const useReseller = () => {
  const queryClient = useQueryClient();

  const dashboardQuery = useQuery({
    queryKey: ['reseller-dashboard'],
    queryFn: fetchResellerDashboard,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const createClientMutation = useMutation({
    mutationFn: createClient,
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['reseller-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['reseller-clients'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao criar cliente');
    }
  });

  const generateCodesMutation = useMutation({
    mutationFn: ({ planId, quantity }: { planId: string; quantity: number }) => 
      generateActivationCodes(planId, quantity),
    onSuccess: (codes) => {
      toast.success(`${codes.length} códigos gerados com sucesso!`);
      queryClient.invalidateQueries({ queryKey: ['reseller-dashboard'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao gerar códigos');
    }
  });

  return {
    // Dashboard data
    dashboard: dashboardQuery.data,
    isDashboardLoading: dashboardQuery.isLoading,
    dashboardError: dashboardQuery.error,
    
    // Client management
    createClient: createClientMutation.mutate,
    isCreatingClient: createClientMutation.isPending,
    
    // Code generation
    generateCodes: generateCodesMutation.mutate,
    isGeneratingCodes: generateCodesMutation.isPending,
    generatedCodes: generateCodesMutation.data,
    
    // Refresh functions
    refetchDashboard: dashboardQuery.refetch
  };
};

export const useResellerClients = (filters?: ClientFilters) => {
  return useQuery({
    queryKey: ['reseller-clients', filters],
    queryFn: () => fetchResellerClients(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};
